import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Skeleton, TextField, Autocomplete,
  Popper, Paper, List, ListItem, ListItemText, ListItemButton, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../services/api';

// 搜尋自動完成組件
export const SearchAutocomplete = ({ onSearch, placeholder = "搜尋商品..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // 取得 Session ID
  const getSessionId = () => {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  };

  // 載入搜尋歷史和趨勢
  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionId = getSessionId();
        const userId = localStorage.getItem('user_id');

        // 取得搜尋歷史
        const historyRes = await api.get(`/search/history?session_id=${sessionId}${userId ? `&user_id=${userId}` : ''}&limit=5`);
        if (historyRes.data.success) {
          setHistory(historyRes.data.data);
        }

        // 取得熱門搜尋
        const trendingRes = await api.get('/search/trending?limit=8');
        if (trendingRes.data.success) {
          setTrending(trendingRes.data.data);
        }
      } catch (error) {
        console.error('載入搜尋資料失敗:', error);
      }
    };
    fetchData();
  }, []);

  // 搜尋建議 (自動完成)
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/search/suggestions?prefix=${encodeURIComponent(searchTerm)}&limit=5`);
        if (response.data.success) {
          setSuggestions(response.data.data);
        }
      } catch (error) {
        console.error('取得搜尋建議失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSearch = (keyword) => {
    if (keyword && keyword.trim()) {
      setSearchTerm('');
      setOpen(false);
      if (onSearch) {
        onSearch(keyword.trim());
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      handleSearch(searchTerm);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
          endAdornment: searchTerm && (
            <IconButton size="small" onClick={() => setSearchTerm('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            bgcolor: 'background.paper'
          }
        }}
      />

      {/* 下拉建議列表 */}
      {open && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 400,
            overflowY: 'auto',
            zIndex: 1300,
            boxShadow: 3
          }}
        >
          {/* 搜尋建議 */}
          {searchTerm.trim().length >= 2 && (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  搜尋建議
                </Typography>
              </Box>
              {loading ? (
                <Box sx={{ px: 2, py: 1 }}>
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </Box>
              ) : suggestions.length > 0 ? (
                <List dense>
                  {suggestions.map((item, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton onClick={() => handleSearch(item.keyword)}>
                        <SearchIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                        <ListItemText
                          primary={item.keyword}
                          secondary={`${item.search_count} 次搜尋`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    無相關建議
                  </Typography>
                </Box>
              )}
              <Divider />
            </>
          )}

          {/* 搜尋歷史 */}
          {history.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HistoryIcon fontSize="small" /> 最近搜尋
                </Typography>
              </Box>
              <List dense>
                {history.map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => handleSearch(item.keyword)}>
                      <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'action.disabled' }} />
                      <ListItemText primary={item.keyword} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Divider />
            </>
          )}

          {/* 熱門搜尋 */}
          {trending.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon fontSize="small" /> 熱門搜尋
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {trending.map((item, index) => (
                  <Chip
                    key={index}
                    label={item.keyword}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => handleSearch(item.keyword)}
                    sx={{ '&:hover': { bgcolor: 'primary.light', color: 'white', borderColor: 'primary.light' } }}
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

// 熱門搜尋標籤列表
export const TrendingSearches = ({ limit = 10, onSearch }) => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const response = await api.get(`/search/trending?limit=${limit}`);
        if (response.data.success) {
          setKeywords(response.data.data);
        }
      } catch (error) {
        console.error('取得熱門搜尋失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, [limit]);

  if (!loading && keywords.length === 0) return null;

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TrendingUpIcon fontSize="small" /> 熱門搜尋：
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {loading
          ? Array(8).fill(0).map((_, i) => (
              <Skeleton key={i} width={70} height={28} sx={{ borderRadius: 2 }} />
            ))
          : keywords.map((item, index) => (
              <Chip
                key={index}
                label={`${item.keyword} (${item.search_count})`}
                size="small"
                variant="outlined"
                color="primary"
                clickable
                icon={<TrendingUpIcon />}
                onClick={() => onSearch && onSearch(item.keyword)}
                sx={{ '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
              />
            ))
        }
      </Box>
    </Box>
  );
};

// 搜尋歷史列表
export const SearchHistoryList = ({ limit = 10, onSearch }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const sessionId = localStorage.getItem('session_id');
        const userId = localStorage.getItem('user_id');

        if (!sessionId) return;

        const response = await api.get(`/search/history?session_id=${sessionId}${userId ? `&user_id=${userId}` : ''}&limit=${limit}`);
        if (response.data.success) {
          setHistory(response.data.data);
        }
      } catch (error) {
        console.error('取得搜尋歷史失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [limit]);

  if (!loading && history.length === 0) return null;

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <HistoryIcon fontSize="small" /> 最近搜尋：
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {loading
          ? Array(5).fill(0).map((_, i) => (
              <Skeleton key={i} width={80} height={28} sx={{ borderRadius: 2 }} />
            ))
          : history.map((item, index) => (
              <Chip
                key={index}
                label={item.keyword}
                size="small"
                variant="outlined"
                clickable
                icon={<HistoryIcon />}
                onClick={() => onSearch && onSearch(item.keyword)}
                sx={{ '&:hover': { bgcolor: 'grey.200' } }}
              />
            ))
        }
      </Box>
    </Box>
  );
};

export default SearchAutocomplete;
